import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Circle, Icon, Stack } from '@chakra-ui/react';
import { BsSearch } from 'react-icons/bs';

import {
	Modal,
	PaginatedListingGrid,
	ListingSearchFiltersForm,
	InfoGuide,
	ListingTagWizard,
} from 'components';
import { NavigationService } from 'services';
import { extractFiltersFromLocation, updateSearchFiltersInUrl } from 'utils/search';
import { ListingSearchFiltersFormData } from 'components/Form/ListingSearchFiltersForm/ListingSearchFiltersForm';
import { QuerySearchParameters } from 'constants/url';
import { useListingSearch } from 'hooks/queries/useSearch';
import { useArtists } from 'hooks/queries/useArtists';
import { useMarkListingAsFake, useMarkListingAsSet, useMarkListingAsOriginal } from 'hooks/queries/useListings';
import { ListingModel } from 'models';
import { JSONObject } from 'models/Api/types';
import { ListingTagType } from 'components/Wizard/ListingTagWizard/ListingTagWizard';

export default function ListingOverviewView() {
	const location = useLocation();
	const navigate = useNavigate();

	const { listing: defaultListingFilters } = extractFiltersFromLocation(location);

	const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
	const [isBulkTagModalOpen, setIsBulkTagModalOpen] = useState(false);
	const [listingSearchFilters, setListingSearchFilters] = useState<ListingSearchFiltersFormData | undefined>(defaultListingFilters);
	const [page, setPage] = useState(1);

	const { data: listingSearchResults, isLoading: listingSearchLoading } = useListingSearch(listingSearchFilters, page);
	const { data: artistList = [] } = useArtists();

	// Listing marking mutations
	const markAsFakeMutation = useMarkListingAsFake();
	const markAsSetMutation = useMarkListingAsSet();
	const markAsOriginalMutation = useMarkListingAsOriginal();

	useEffect(() => {
		const { listing } = extractFiltersFromLocation(location);
		setListingSearchFilters(listing);
	}, [location]);

	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const pageParam = Number(queryParams.get(QuerySearchParameters.PAGE) || '1');
		setPage(Number.isNaN(pageParam) ? 1 : pageParam);
	}, [location.search]);

	const openFiltersModal = () => setIsFiltersModalOpen(true);
	const closeFiltersModal = () => setIsFiltersModalOpen(false);

	const handleListingSearchSubmit = (searchQuery: ListingSearchFiltersFormData) => {
		setListingSearchFilters(searchQuery);
		setPage(1);
		updateSearchFiltersInUrl({
			location,
			navigate,
			filters: searchQuery,
			filterParamKey: QuerySearchParameters.LISTING_FILTERS,
			paramMappings: [
				{ key: QuerySearchParameters.SORT_BY, value: searchQuery.sortBy },
				{ key: QuerySearchParameters.SORT_TYPE, value: searchQuery.sortType },
				{ key: QuerySearchParameters.TYPE, value: searchQuery.type },
			],
		});
		closeFiltersModal();
	};

	const handleListingSearchReset = () => {
		setListingSearchFilters(undefined);
		setPage(1);
		updateSearchFiltersInUrl({
			location,
			navigate,
			filters: undefined,
			filterParamKey: QuerySearchParameters.LISTING_FILTERS,
			paramMappings: [
				{ key: QuerySearchParameters.SORT_BY, value: null },
				{ key: QuerySearchParameters.SORT_TYPE, value: null },
				{ key: QuerySearchParameters.TYPE, value: null },
			],
		});
	};


	const updatePageInURL = (pageNumber: number) => {
		const queryParams = new URLSearchParams(location.search);
		queryParams.set(QuerySearchParameters.PAGE, pageNumber.toString());
		navigate(`${location.pathname}?${queryParams.toString()}`, { replace: true });
	};

	const handleListingsPageSelect = (pageNumber: number) => {
		setPage(pageNumber);
		updatePageInURL(pageNumber);
	};

	const handleListingNameClick = (listingId: string, listingName: string) => {
		navigate(NavigationService.getListingDetailsPath(listingId, listingName));
	};

	const handleListingItemClick = (itemId: string, itemName: string) => {
		navigate(NavigationService.getItemDetailsPath(itemId, itemName));
	};

	const handleListingArtistClick = (artistId: string, artistName: string) => {
		navigate(NavigationService.getArtistDetailsPath(artistId, artistName));
	};

	const handleBulkTagClick = useCallback(() => {
		setIsBulkTagModalOpen(true);
	}, []);

	const handleBulkTagSubmit = useCallback((listings: ListingModel[], tagType: ListingTagType, data: JSONObject) => {
		const listingIds = listings.map(l => l.id);

		if (tagType === 'FAKE') {
			markAsFakeMutation.mutate(listingIds);
		} else if (tagType === 'SET') {
			markAsSetMutation.mutate(listingIds);
		} else if (tagType === 'ORIGINAL') {
			markAsOriginalMutation.mutate(listingIds);
		}

		setIsBulkTagModalOpen(false);
	}, [markAsFakeMutation, markAsSetMutation, markAsOriginalMutation]);

	return (
		<Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
			<Stack gap={4}>
				<InfoGuide text="Note: We only show publicly available, verifiable auction house sales data." dismissable />
				<PaginatedListingGrid
					paginatedListings={listingSearchResults}
					onPageSelect={handleListingsPageSelect}
					scrollMode={false}
					onListingNameClick={handleListingNameClick}
					onListingItemClick={handleListingItemClick}
					onListingArtistClick={handleListingArtistClick}
					onBulkTagClick={handleBulkTagClick}
					isLoading={listingSearchLoading}
				/>
				<Circle
					size="40px"
					bg="gray.300"
					left="50%"
					transform="translateX(-50%)"
					position="fixed"
					bottom={100}
					cursor="pointer"
					onClick={openFiltersModal}
					zIndex="overlay"
				>
					<Icon as={BsSearch} color="white.100" />
				</Circle>
			</Stack>

			<Modal
				isOpen={isFiltersModalOpen}
				onClose={closeFiltersModal}
				showSubmitButton={false}
				showCloseButton={false}
				title="Listing Search Filters"
				size="6xl"
						content={
							<ListingSearchFiltersForm
								artistList={artistList}
								defaultFormData={listingSearchFilters}
								onSubmit={handleListingSearchSubmit}
								onReset={handleListingSearchReset}
								submitLoading={listingSearchLoading}
							/>
						}
					/>

			<Modal
				title="Bulk Tag Listings"
				content={
					<ListingTagWizard
						listings={listingSearchResults?.data || []}
						onSubmit={handleBulkTagSubmit}
						isLoading={markAsFakeMutation.isPending || markAsSetMutation.isPending || markAsOriginalMutation.isPending}
					/>
				}
				isOpen={isBulkTagModalOpen}
				onClose={() => setIsBulkTagModalOpen(false)}
				showCloseButton={false}
				showSubmitButton={false}
				size="4xl"
			/>
		</Box>
	);
}
